import { computed, ErrorHandler, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  ChatDaemon,
  ChatSystemNotificationController,
} from 'ias-ppr-mt-chat-client';
import { NativePushService } from '@core/services/native-shell/native-push.service';
import { NativeShellService } from '@core/services/native-shell/native-shell.service';
import {
  EMPTY,
  catchError,
  defer,
  distinctUntilChanged,
  from,
  retry,
  switchMap,
  timer,
} from 'rxjs';

export function nativePushPermissionInitializer(): void {
  const push = inject(NativePushService);
  const controller = inject(ChatSystemNotificationController);
  const shell = inject(NativeShellService);
  const chat = inject(ChatDaemon);
  const errors = inject(ErrorHandler);
  push.changed$
    .pipe(takeUntilDestroyed())
    .subscribe(() => controller.refreshPermission());

  // The shared preference remains authoritative after a restored native installation.
  // A bound, idle controller with granted permission projects the actual preference.
  // Failure/unavailable state is not a user request to disable native delivery.
  const failedDisable = new Set([
    'MT_CHAT.SYSTEM_NOTIFICATIONS.BACKGROUND_DISCONNECT_FAILED',
    'MT_CHAT.SYSTEM_NOTIFICATIONS.BACKGROUND_DISCONNECT_TIMEOUT',
  ]);
  const staleInstallation = computed(() => {
    const generation = shell.generation();
    const userId = chat.currentUser()?.id;
    const state = push.state();
    const status = controller.statusLabelKey();
    if (
      !push.available() ||
      generation === null ||
      !userId ||
      !state?.enabled ||
      controller.permission() !== 'granted' ||
      controller.enabled() ||
      (status !== null && !failedDisable.has(status)) ||
      controller.requestPending()
    )
      return null;
    return { generation, userId, state };
  });
  toObservable(staleInstallation)
    .pipe(
      distinctUntilChanged(
        (a, b) =>
          a?.generation === b?.generation &&
          a?.userId === b?.userId &&
          a?.state === b?.state,
      ),
      switchMap((value) =>
        value
          ? defer(() => from(push.setEnabled(false, value.generation))).pipe(
              // A bridge timeout must not leave a confirmed user disable unapplied.
              // New native snapshots also restart this bounded repair; re-enable cancels it.
              retry({
                count: 2,
                delay: (_error, attempt) => timer(attempt * 1000),
              }),
              catchError((error) => {
                if (value.generation === shell.generation())
                  errors.handleError(error);
                return EMPTY;
              }),
            )
          : EMPTY,
      ),
      takeUntilDestroyed(),
    )
    .subscribe();
}
